// GanttOprawaConnections.jsx
import React, { useMemo } from 'react';

const PALETA_KOLOROW = [
  '#3f51b5', '#00bcd4', '#f4a261', '#9c27b0', '#8bc34a',
  '#e63946', '#2a9d8f', '#e91e63', '#ff9800', '#795548',
];

const KOLOR_DOMYSLNY = '#888888';

const zawieraElement = (elementyIdText, searchElementId) => {
  if (elementyIdText === null || elementyIdText === undefined || searchElementId === null || searchElementId === undefined) {
    return false;
  }
  const target = String(searchElementId).trim();

  if (Array.isArray(elementyIdText)) {
    return elementyIdText.map(String).includes(target);
  }

  const lista = String(elementyIdText).split(',').map(s => s.trim()).filter(Boolean);
  return lista.includes(target);
};

const GanttOprawaConnections = ({
  groupedStages = {},
  processorIds = [],
  rowYPositions = {},
  minDate,
  maxDate,
  totalChartWidth,
  selectedZamowienieId,
  showOprawaConnections,
  calculateBarProperties
}) => {
  const connectionLines = useMemo(() => {
    if (!showOprawaConnections || selectedZamowienieId === undefined || selectedZamowienieId === null) {
      return [];
    }
    if (!minDate || !maxDate || !totalChartWidth || typeof calculateBarProperties !== 'function') {
      return [];
    }

    const selectedZamowienieStr = String(selectedZamowienieId);
    const targetStages = [];

    // 1. Zbieramy wszystkie etapy dla wybranego zamówienia
    processorIds.forEach((processorId) => {
      const rowStages = groupedStages[processorId] || [];
      const y = rowYPositions[processorId] ?? 0;

      rowStages.forEach((stage) => {
        if (!stage || stage.zamowienie_id === undefined || stage.zamowienie_id === null) return;

        if (String(stage.zamowienie_id) === selectedZamowienieStr) {
          const barProps = calculateBarProperties(stage.start, stage.end) || { leftPx: 0, widthPx: 0 };
          const { leftPx = 0, widthPx = 0 } = barProps;

          const elemIdStr = stage.element_id !== undefined && stage.element_id !== null 
            ? String(stage.element_id) 
            : null;

          targetStages.push({
            ...stage,
            stageId: String(stage.global_id || stage.id),
            elementIdStr: elemIdStr,
            indeks: Number(stage.indeks_procesu ?? stage.indeks ?? 0),
            procesNazwaId: Number(stage.proces_nazwa_id),
            outPoint: { x: leftPx + widthPx, y },
            inPoint: { x: leftPx, y }
          });
        }
      });
    });

    if (targetStages.length === 0) return [];

    // 2. Filtrujemy Oprawę (proces_nazwa_id === 6)
    const oprawaStages = targetStages.filter(stage => stage.procesNazwaId === 6);
    if (oprawaStages.length === 0) return [];

    // 3. Rozdzielamy etapy na grupy wg element_id (z wyłączeniem oprawy)
    const stagesByElement = {};

    targetStages.forEach((stage) => {
      if (stage.procesNazwaId === 6) return; // Oprawa jest celem
      if (!stage.elementIdStr) return;

      const elemId = stage.elementIdStr;
      if (!stagesByElement[elemId]) {
        stagesByElement[elemId] = [];
      }
      stagesByElement[elemId].push(stage);
    });

    // 4. Dla każdego element_id znajdujemy jego MKSYMALNY indeks w grupie
    // a następnie bierzemy WSZYSTKIE etapy o tym najwyższym indeksie dla tego elementu
    const lastStagesByElement = {};

    Object.keys(stagesByElement).forEach((elemId) => {
      const elemStages = stagesByElement[elemId];
      if (elemStages.length === 0) return;

      // Znajdujemy największą liczbę indeksu w obrębie TEGO JEDNEGO elementu
      const maxIndeks = Math.max(...elemStages.map(s => s.indeks));

      // Bierzemy WSZYSTKIE etapy dla tego elementu, które mają ten maksymalny indeks
      // (dzięki temu jeśli dla elementu 1 są np. 3 arkusze z indeksem 4, weźmie je wszystkie 3,
      // ale odrzuci druk/krojenie z indeksami 1, 2, 3)
      lastStagesByElement[elemId] = elemStages.filter(s => s.indeks === maxIndeks);
    });

    // 5. Przypisanie kolorów dla elementów
    const elementColorMap = {};
    Object.keys(lastStagesByElement).forEach((elemId, index) => {
      elementColorMap[elemId] = index < PALETA_KOLOROW.length
        ? PALETA_KOLOROW[index]
        : KOLOR_DOMYSLNY;
    });

    const lines = [];

    // 6. Łączenie wyznaczonych ostatnich etapów z oprawą
    Object.keys(lastStagesByElement).forEach((elemId) => {
      const stagesList = lastStagesByElement[elemId];

      const matchingOprawy = oprawaStages.filter(oprawaStage =>
        zawieraElement(oprawaStage.elementy_id, elemId)
      );

      stagesList.forEach((lastStage) => {
        matchingOprawy.forEach((oprawaStage) => {
          const strokeColor = elementColorMap[elemId] || KOLOR_DOMYSLNY;

          lines.push({
            id: `oprawa_${elemId}_${lastStage.stageId}_to_${oprawaStage.stageId}`,
            elementId: elemId,
            from: lastStage.outPoint,
            to: oprawaStage.inPoint,
            strokeColor
          });
        });
      });
    });

    return lines;
  }, [
    groupedStages,
    processorIds,
    rowYPositions,
    minDate,
    maxDate,
    totalChartWidth,
    selectedZamowienieId,
    showOprawaConnections,
    calculateBarProperties
  ]);

  if (connectionLines.length === 0) return null;

  const uniqueElements = Array.from(
    new Map(
      connectionLines
        .filter(l => l.elementId !== null && l.elementId !== undefined)
        .map(line => [line.elementId, line.strokeColor])
    ).entries()
  );

  return (
    <svg
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 11
      }}
    >
      <defs>
        {uniqueElements.map(([elementId, color]) => (
          <marker
            key={`marker-oprawa-${elementId}`}
            id={`arrowhead-oprawa-${elementId}`}
            markerWidth="6"
            markerHeight="6"
            refX="5"
            refY="3"
            orient="auto"
          >
            <polygon points="0 0, 6 3, 0 6" fill={color} />
          </marker>
        ))}
      </defs>

      {connectionLines.map((line) => {
        const { from, to, id, strokeColor, elementId } = line;
        const deltaX = Math.max(20, Math.abs(to.x - from.x) / 2);
        const pathData = `M ${from.x} ${from.y} C ${from.x + deltaX} ${from.y}, ${to.x - deltaX} ${to.y}, ${to.x} ${to.y}`;

        return (
          <g key={`connection-oprawa-${id}`}>
            <path
              d={pathData}
              fill="none"
              stroke={strokeColor}
              strokeWidth="2.5"
              strokeDasharray="4 2"
              markerEnd={`url(#arrowhead-oprawa-${elementId})`}
            />
            <circle cx={from.x} cy={from.y} r="3.5" fill={strokeColor} />
          </g>
        );
      })}
    </svg>
  );
};

export default React.memo(GanttOprawaConnections);